import { DunningBlockReason } from "@/api-schema-enums";
import { Button, ButtonColorVariant, ButtonVariant } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useId } from "react";
import { useTranslation } from "react-i18next";
import { getDunningBlockReasonOptions } from "../_helpers/dunning-block-reasons";

interface DunningBlockDialogProps {
  open: boolean;
  reason: DunningBlockReason;
  onReasonChange: (reason: DunningBlockReason) => void;
  onConfirm: () => void;
  onCancel: () => void;
  isLoading?: boolean;
}

export function DunningBlockDialog({
  open,
  reason,
  onReasonChange,
  onConfirm,
  onCancel,
  isLoading = false,
}: DunningBlockDialogProps) {
  const { t, i18n } = useTranslation(["fia", "common"]);
  const reasonId = useId();
  const title = t("fia:SetDunningBlock");
  const reasonOptions = getDunningBlockReasonOptions(t, i18n.language);

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onCancel()}>
      <DialogContent className="sm:max-w-[440px]">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{t("fia:DunningBlockDialogDescription")}</DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-2 py-4">
          <Label htmlFor={reasonId}>{t("fia:DunningBlockReason")}</Label>
          <Select value={reason} onValueChange={(value) => onReasonChange(value as DunningBlockReason)}>
            <SelectTrigger id={reasonId}>
              <SelectValue placeholder={t("fia:SelectDunningBlockReason")} />
            </SelectTrigger>
            <SelectContent>
              {reasonOptions.map(({ id, name }) => (
                <SelectItem key={id} value={id}>
                  {name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <DialogFooter>
          <Button
            variant={ButtonVariant.Outlined}
            colorVariant={ButtonColorVariant.Secondary}
            onClick={onCancel}
            disabled={isLoading}
          >
            {t("common:Cancel")}
          </Button>
          <Button onClick={onConfirm} disabled={isLoading}>
            {title}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
